import { createSignal, onMount, Show, For, type Component } from "solid-js";
import {
  listActivities,
  createComment,
  type Activity,
  type ActivityListResponse,
} from "../../../api/workItemActivities";
import type { BoardEvent, WorkItemId } from "../../../api/types";
import { useWebSocket } from "../../../hooks/useWebSocket";
import { handleCopyCodeBlockClick } from "../../../utils/copyCodeBlock";
import { ActivityItem } from "./ActivityItem";
import { CommentInput } from "./CommentInput";

export interface ActivityThreadProps {
  workItemId: WorkItemId;
}

export const ActivityThread: Component<ActivityThreadProps> = (props) => {
  // Server returns newest-first; we keep them in display order (oldest top).
  const [activities, setActivities] = createSignal<Activity[]>([]);
  const [cursor, setCursor] = createSignal<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = createSignal(true);
  const [isLoadingMore, setIsLoadingMore] = createSignal(false);
  const [loadError, setLoadError] = createSignal<string | null>(null);

  const insertDedup = (incoming: Activity): void => {
    setActivities((prev) =>
      prev.some((a) => a.id === incoming.id) ? prev : [...prev, incoming],
    );
  };

  const applyInitial = (resp: ActivityListResponse): void => {
    // Reverse to display oldest -> newest.
    setActivities([...resp.items].reverse());
    setCursor(resp.next_cursor);
  };

  const applyOlder = (resp: ActivityListResponse): void => {
    setActivities((prev) => [...[...resp.items].reverse(), ...prev]);
    setCursor(resp.next_cursor);
  };

  const fetchInitial = async (): Promise<void> => {
    setIsInitialLoading(true);
    setLoadError(null);
    try {
      const resp = await listActivities(props.workItemId);
      applyInitial(resp);
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Failed to load activity",
      );
    } finally {
      setIsInitialLoading(false);
    }
  };

  const loadOlder = async (): Promise<void> => {
    const c = cursor();
    if (!c || isLoadingMore()) return;
    setIsLoadingMore(true);
    try {
      const resp = await listActivities(props.workItemId, { cursor: c });
      applyOlder(resp);
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Failed to load older activity",
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSubmit = async (body: string): Promise<void> => {
    const created = await createComment(props.workItemId, body);
    insertDedup(created);
  };

  // Event delegation for the copy-to-clipboard buttons renderMarkdown emits
  // beside fenced code blocks in comment/agent-update bodies (rendered via
  // innerHTML inside ActivityItem).
  const handleActivityClick = (e: MouseEvent) => {
    if (!handleCopyCodeBlockClick(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  onMount(() => {
    void fetchInitial();
  });

  useWebSocket({
    onMessage: (event: BoardEvent) => {
      if (
        event.type === "ActivityAdded" &&
        event.payload.work_item_id === props.workItemId
      ) {
        insertDedup(event.payload.activity);
      }
    },
  });

  return (
    <section
      data-testid="activity-thread"
      aria-labelledby="activity-thread-heading"
      class="mt-6 flex flex-col gap-3"
      onClick={handleActivityClick}
    >
      <h2 id="activity-thread-heading" class="text-base font-semibold text-ink">
        Activity
      </h2>

      <Show when={cursor() !== null}>
        <div class="flex justify-center">
          <button
            type="button"
            onClick={loadOlder}
            disabled={isLoadingMore()}
            class="rounded-md border border-line bg-surface px-3 py-1 text-xs text-ink hover:bg-sunken hover:border-line-strong focus-ring disabled:opacity-50"
          >
            {isLoadingMore() ? "Loading…" : "Load older"}
          </button>
        </div>
      </Show>

      <Show when={loadError()}>
        <p class="text-sm text-signal-red" data-testid="activity-load-error">
          {loadError()}
        </p>
      </Show>

      <Show
        when={!isInitialLoading()}
        fallback={
          <p class="text-sm text-ink-3" data-testid="activity-loading">
            Loading activity…
          </p>
        }
      >
        <Show
          when={activities().length > 0}
          fallback={
            <p class="text-sm text-ink-3" data-testid="activity-empty">
              No activity yet. Be the first to comment.
            </p>
          }
        >
          <ul class="flex flex-col gap-2">
            <For each={activities()}>
              {(a) => (
                <li>
                  <ActivityItem activity={a} />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>

      <CommentInput onSubmit={handleSubmit} />
    </section>
  );
};
